import { z } from 'zod';

import type { StaffProfileStoresItem } from '@nucleus-commerce/api-client/admin';

const LOCAL_INPUT = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/;

const localTime = z
  .string()
  .trim()
  .refine((v) => v === '' || LOCAL_INPUT.test(v), { error: 'products:bulk.schedule.invalidTime' });

/**
 * "Schedule publishing…" on the products index: the stores and one or both times as
 * `datetime-local` values on one store's wall clock (see `scheduleZone`). Order is checked here;
 * "must be in the future" is the backend's, the clock moves.
 */
export const bulkScheduleSchema = z
  .object({
    storeIds: z.array(z.string()).min(1, { error: 'products:bulk.schedule.storesRequired' }),
    publishAt: localTime,
    unpublishAt: localTime,
  })
  .superRefine((v, ctx) => {
    if (!v.publishAt && !v.unpublishAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['publishAt'],
        message: 'products:bulk.schedule.timeRequired',
      });
    }
    // Same-zone wall-clock strings compare in time order.
    if (v.publishAt && v.unpublishAt && v.unpublishAt <= v.publishAt) {
      ctx.addIssue({
        code: 'custom',
        path: ['unpublishAt'],
        message: 'products:bulk.schedule.unpublishAfterPublish',
      });
    }
  });

export type BulkScheduleForm = z.infer<typeof bulkScheduleSchema>;

type ZonedStore = Pick<StaffProfileStoresItem, 'id' | 'name' | 'timezone'>;

/**
 * The clock the times are entered on: the zone of the store ticked first (`storeIds` keeps tick
 * order; the first store in the list when none is ticked yet), so ticking more stores never moves
 * it. `otherZones` are the ticked stores on another zone, in list order. The request carries
 * instants, so every store flips at the same moment, at its own local time.
 */
export function scheduleZone(
  stores: readonly ZonedStore[],
  storeIds: readonly string[],
  zoneOf: (timezone: string) => string,
): { timeZone: string; otherZones: ZonedStore[] } {
  const chosen = stores.filter((store) => storeIds.includes(store.id));
  const first = stores.find((store) => store.id === storeIds[0]) ?? stores[0];
  const timeZone = zoneOf(first?.timezone ?? 'UTC');
  return {
    timeZone,
    otherZones: chosen.filter((store) => zoneOf(store.timezone) !== timeZone),
  };
}
